import * as React from "react";
import {
  ApolloError,
  gql,
  useMutation,
} from "@apollo/client";
import {
    DeleteOfferPointsDeductionMutationMutation,
    DeleteOfferPointsDeductionMutationMutationVariables
} from "../../../../../schemaTypes";
import { NormalizedErrorMap } from "../../../../../types/NormalizedErrorMap";
import { normalizeErrors } from "../../../../../utils/normalizeErrors";

export const deleteOfferPointsDeductionMutation = gql`
  mutation DeleteOfferPointsDeductionMutation(
    $id: String!
  ) {
    deleteOfferPointsDeduction(
      id: $id
    ) {
      errors {
        path
        message
      }
      message
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: DeleteOfferPointsDeductionMutationMutationVariables
      ) => Promise<{
        errors: NormalizedErrorMap | null;
        message?: string;
      }>;
      loading: boolean;
      error?: ApolloError;
    }
  ) => React.ReactElement | null;
}

export const DeleteOfferPointsDeductionController: React.FC<Props> = ({
  children,
  onMessage,
}) => {
  const [deleteOfferPointsDeductionMutationFn, { loading, error }] =
    useMutation<
      DeleteOfferPointsDeductionMutationMutation,
      DeleteOfferPointsDeductionMutationMutationVariables
    >(deleteOfferPointsDeductionMutation, {
      // Drop the deleted deduction from the list if it's on screen.
      refetchQueries: ["FindAllOfferPointsDeductionsQuery"],
    });

  const submit = async (
    values: DeleteOfferPointsDeductionMutationMutationVariables
  ) => {
    const id = typeof values.id === "string" ? values.id.trim() : "";
    if (!id) {
      return {
        errors: normalizeErrors([
          { path: "id", message: "Offer points deduction id is required" },
        ]),
        message: undefined,
      };
    }

    const response = await deleteOfferPointsDeductionMutationFn({
      variables: { ...values, id },
    });

    const data = response.data?.deleteOfferPointsDeduction;

    const errors = data?.errors
      ? normalizeErrors(data.errors)
      : null;

    if (data?.message && onMessage) {
      onMessage(data.message);
    }

    return {
      errors,
      message: data?.message ?? undefined,
    };
  };

  return children({ submit, loading, error });
};
